/**
 * Calculation Utilities for Horseshoe Tunnel
 * Alpha, Beta, Quadrants, ApDir, Dopp calculations
 */

import { toRadians, toDegree } from "./mathUtils";

/**
 * Calculate Alpha (angle between strike and tunnel direction)
 * @param {number} D - Tunnel direction in degrees
 * @param {number} Dir - Dip direction in degrees
 * @returns {number} Alpha in degrees
 */
export const calculateAlpha = (D, Dir) => {
  let Dnew = D > 180 ? D - 180 : D;
  let Dirnew = Dir > 180 ? Dir - 180 : Dir;
  let Value = Math.abs(Dnew - Dirnew);

  if (Value <= 90) {
    return 90 - Value;
  } else {
    return Value - 90;
  }
};

/**
 * Calculate Beta (apparent dip along tunnel)
 * @param {number} amt - Dip amount in degrees
 * @param {number} Alpha - Alpha angle in degrees
 * @returns {number} Beta in degrees
 */
export const calculateBeta = (amt, Alpha) => {
  // amt and Alpha are in DEGREES, convert to radians for Math functions
  let TanBeta = Math.tan(toRadians(amt)) * Math.sin(toRadians(Alpha));
  // Math.atan returns RADIANS, convert to degrees
  return toDegree(Math.atan(TanBeta));
};

/**
 * Calculate quadrants Q1-Q4 based on tunnel direction D
 * @param {number} D - Tunnel direction in degrees
 * @returns {Object} Quadrant boundaries in degrees
 */
export const calculateQuadrants = (D) => {
  let Q1max, Q1min, Q2max, Q2min, Q3max, Q3min, Q4max, Q4min;
  let Q1temp, Q2temp, Q3temp, Q4temp;

  // Quadrant 1
  Q1max = D;
  Q1min = D - 90;
  if (Q1min < 0) {
    Q1temp = 360 + Q1min;
    Q1min = Q1temp;
  }

  // Quadrant 2
  Q2min = D;
  Q2temp = D + 90;
  if (Q2temp > 360) {
    Q2max = Q2temp - 360;
  } else {
    Q2max = Q2temp;
  }

  // Quadrant 3
  Q3min = Q2max;
  Q3temp = Q3min + 90;
  if (Q3temp > 360) {
    Q3max = Q3temp - 360;
  } else {
    Q3max = Q3temp;
  }

  // Quadrant 4
  Q4min = Q3max;
  Q4temp = Q4min + 90;
  if (Q4temp > 360) {
    Q4max = Q4temp - 360;
  } else {
    Q4max = Q4temp;
  }

  return { Q1min, Q1max, Q2min, Q2max, Q3min, Q3max, Q4min, Q4max };
};

/**
 * Calculate Dopp (direction opposite to tunnel direction)
 * @param {number} D - Tunnel direction in degrees
 * @returns {number} Dopp in degrees
 */
export const calculateDopp = (D) => {
  if (D <= 180) {
    return D + 180;
  } else {
    return D - 180;
  }
};

/**
 * Calculate ApDir (apparent direction)
 * @param {number} D - Tunnel direction in degrees
 * @param {number} Dir - Dip direction in degrees
 * @param {Object} quadrants - Quadrant boundaries from calculateQuadrants
 * @returns {number} ApDir in degrees
 */
export const calculateApDir = (D, Dir, quadrants) => {
  let Dopp = calculateDopp(D);
  let { Q1min, Q1max, Q2min, Q2max, Q3min, Q3max, Q4min, Q4max } = quadrants;

  if (Dir === D) {
    return D;
  }

  // Dopp is 360 for a due-south drive, which Dir 0 must match.
  if (Dir % 360 === Dopp % 360) {
    return Dopp;
  }

  // Check quadrants
  if (
    (Q1min < Dir && Dir < Q1min + 90) ||
    (Dir < Q1max && Dir > D - 90)
  ) {
    return D;
  }

  if (
    (Q2min < Dir && Dir < Q2min + 90) ||
    (Dir < Q2max && Dir > Q2max - 90)
  ) {
    return D;
  }

  if (
    (Q3min < Dir && Dir < Q3min + 90) ||
    (Dir < Q3max && Dir > Q3max - 90)
  ) {
    return Dopp;
  }

  if (
    (Q4min < Dir && Dir < Q4min + 90) ||
    (Dir < Q4max && Dir > Q4max - 90)
  ) {
    return Dopp;
  }

  return D; // Default
};

/**
 * Calculate crown geometry values for horseshoe tunnel
 * @param {number} ws - Width at SPL in meters
 * @param {number} height - Total tunnel height in meters
 * @param {number} hw - Wall height in meters
 * @returns {Object} Crown geometry: Whalf, HC, R, Theta, Arc, ArcFull, HWn
 */
export const calculateCrownGeometry = (ws, height, hw) => {
  const Whalf = ws / 2;
  const HC = height - hw;
  const R = (Math.pow(Whalf, 2) + Math.pow(HC, 2)) / (2 * HC);
  const Theta = toDegree(Math.atan((R - HC) / Whalf));
  const Arc = (Math.PI * R) / 2 - ((Math.PI * R) / 2) * (Theta / 90);
  const ArcFull = Arc * 2;
  const HWn = height - R;

  return { Whalf, HC, R, Theta, Arc, ArcFull, HWn };
};
